import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from "@emotion/styled";
import { Link } from 'react-router-dom';
import ContentCollapsingSection from '../structure/ContentCollapsingSection';
import CustomScrollbars from '../structure/CustomScrollbars';
import AnalyticsRequestFilter from '../../models/analytics/AnalyticsRequestFilter';
import SessionAnalyticsContainer from '../../hooks/SessionAnalyticsContainer';
import { SESSION_DATE_FORMAT } from '../../models/analytics/AnalyticsDateFormat';
import moment from 'moment';
import { getApplicationEngagements, getOrganizationEngagements, OrganizationEngagement } from '../../api/controllers/engagementAnalyticsReporting';
import { mapEngagementsToSessionReviews, computeEngagementStats, loadEngagementsPaged } from '../../utils/engagementAnalyticsTransformers';
import { TagFilterModal } from './TagFilterModal';
import SearchField from '../forms/SearchField';
import TotalCard from '../charts/TotalCard';
import SessionReview from '../../models/analytics/SessionReview';
import { indexOf } from 'lodash';

import ApplicationModel from '../../models/applications/api/ApplicationModel';
import * as voicifyApi from '../../api';
import { getCategoryName, TAGS } from '../../constants/analyticsConstants/generativeCallReview';
import { useTagConfig } from '../../hooks/TagConfigContainer';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from '../../constants/featureFlagIds';
import { color_text_light, color_colors_decline, color_shades_light, color_shades_lightest, color_colors_ocean, color_defaults_white, color_text_default, color_shades_darker, color_shades_dark, color_variants_ocean_light_1, color_shades_lighter } from '../../constants/colors';
import { breakpoint_small, breakpoint_small_landscape } from '../../constants/breakpoints';
import Button from '../general/Button';
import TooltipWrapper from '../general/TooltipWrapper';
import ToggleIndicator from '../general/ToggleIndicator';
import SessionTags from './SessionTags';
import { canViewInternalVoicifyTags, filterInternalVoicifyTags } from '../../utils/tagVisibility';

const outputIcon = require('../../content/images/device.svg');
const assistantIssueIcon = require('../../content/images/analytics-icons/assistant-issue.svg');
const transferredIcon = require('../../content/images/analytics-icons/transfer-call.svg');
const orderPlacedIcon = require('../../content/images/analytics-icons/order-placed.svg');
const copyIcon = require('../../content/images/organization-settings/copy-blue.svg');

const TAGS_WITH_SUMMARY_CARDS = []; // Can update this with tag names in the future if we want to show summary cards for specific tags
const SESSIONS_PER_PAGE = 50; // Number of sessions to show per page
const AUTO_REFRESH_INTERVAL_MINUTES = 5; // Auto-refresh interval in minutes for cross-org mode when end date is after now

// Styled components defined outside the component to prevent recreation on every render
const StyledContentCollapsingSection = styled(ContentCollapsingSection)`
    margin: 0;
    .card-title-container h4 a {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
        max-width: 100%;
        vertical-align: middle;
    };
`;

const ScrollableSessionContainer = styled.div`
    position: relative;
    width: 100%;
`;

const SessionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 0 16px;
`;

const NoDataMessage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    color: #6b7280;
    font-style: italic;
    text-align: center;
    background: #f9fafb;
    border-radius: 8px;
    margin: 8px 0;
`;

const NoTagsMessage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 32px 16px;
    color: #6b7280;
    font-style: italic;
    text-align: center;
    background: #f9fafb;
    border-radius: 8px;
    margin: 16px 32px;
`;

const SessionRow = styled.div<{ isCollapsed?: boolean }>`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;
    padding: 8px 24px;
    margin: 0;
    background: #f7f9fa;        
    border: 1px solid #d8d8d8;  
    border-radius: ${props => props.isCollapsed ? '8px' : '8px 8px 0 0'};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    gap: 16px;
`;

const SummaryDisplay = styled.div`
    width: 100%;
    box-sizing: border-box;
    display: block;
    padding: 16px 24px;
    margin: 0;
    background: #ffffff;
    border: 1px solid #d8d8d8;
    border-top: none;
    border-radius: 0 0 8px 8px;
    line-height: 1.5;
    
    div {
        margin-bottom: 4px;
        
        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const CollapsingSectionSubtitle = styled.label<{ hasMatches?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    ${props => props.hasMatches === false ? `color: #9ca3af;` : ''}
`;

const FilterResultsText = styled.span`
    font-weight: normal;
    color: ${color_text_light};
`;

const FilterResultsContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    font-size: 14px;
`;

const FilterTagsDisplay = styled.div`
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
`;

const TitleStatsText = styled.span`
    font-weight: normal;
    color: ${color_text_light};
    margin-left: 8px;
    font-size: 14px;
`;

const FilterTagPill = styled.span<{ tagColors?: { background: string; color: string; border: string; }; isExcluded?: boolean }>`
    display: inline-flex;
    align-items: center;
    background: ${props => {
        if (props.isExcluded) {
            // Keep original background color for excluded tags, but make it slightly muted
            return props.tagColors?.background || '#f3f4f6';
        }
        return props.tagColors?.background || '#f3f4f6';
    }};
    color: ${props => {
        if (props.isExcluded) {
            // Keep original color but make it slightly darker/muted
            return props.tagColors?.color || '#6b7280';
        }
        return props.tagColors?.color || '#6b7280';
    }};
    border: ${props => props.isExcluded ? `1px solid ${props.tagColors?.border || '#d1d5db'}` : 'none'};
    font-size: 0.75rem;
    padding: 0px 6px;
    border-radius: 9999px;
    white-space: nowrap;
    height: 18px;
    font-style: normal;
    opacity: ${props => props.isExcluded ? '0.7' : '1'};
`;

const NotPrefix = styled.span`
    color: ${color_colors_decline};
    font-weight: 600;
    margin-right: 4px;
`;

const FilterNoResultsMessage = styled.div`
    margin: 32px 0;
    padding: 16px 32px;
`;

const GenerativeSessionReviewRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    ${breakpoint_small} {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
    }
`;

const CustomSearchWrapper = styled.div`
    margin-right: auto;
    
    ${breakpoint_small} {
        margin-right: 0;
        width: 100%;
    }
`;

const CustomSearch = styled(SearchField)`   
    margin: 0;
    padding: 0;
    div > .field-container {
        height: 32px;
        box-shadow: none;
        border-radius: 8px;
        margin: 0;
        padding: 0;
    }
    input::placeholder {
        font-size: 0.75rem;
    }
`;

const SessionLink = styled(Link)`
    all: unset;
    width: 100%;
    cursor: pointer;
    display: block;
    &:hover ${SessionRow}, &:focus ${SessionRow} {
    background: #eaf3ff;
    }
    &:hover ${SummaryDisplay}, &:focus ${SummaryDisplay} {
    background: #f9fbff;
    }
`;

const CollapseButton = styled.button<{ isCollapsed: boolean }>`
    background: none;
    border: none;
    padding: 12px;
    cursor: pointer;
    color: #6b7280;
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    min-height: 32px;
    border-radius: 4px;
    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }
    &:focus {
        outline: none;
        background-color: rgba(0, 0, 0, 0.1);
    }
    & .toggle-indicator {
        width: 6px;
        height: 6px;
    }
`;

const CollapsibleSessionCard = styled.div`
    width: 100%;
    transition: opacity 0.2s ease;
`;

const SessionLeftSide = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const TagsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    
    ${breakpoint_small} {
        display: none;
    }
    
    ${breakpoint_small_landscape} {
        display: none;
    }
`;

const NoTagsPill = styled.span`
    display: inline-flex;
    align-items: center;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 0.75rem;
    padding: 0px 6px;
    border-radius: 9999px;
    white-space: nowrap;
    height: 18px;
`;

const StyledTotalCard = styled(TotalCard)`
    padding: 32px;
    text-align: center;
    height: auto;
    margin: 0px 32px 32px 32px;
`;

const CallBreakdownTitle = styled.h3`
    font-size: 1.125rem;
    font-weight: 500;
    color: #374151;
    margin: 0;
    padding: 0;
`;

const CallBreakdownTitleSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    
    ${breakpoint_small} {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
`;

const LoadingLogBox = styled.div`
    background: #f8f9fa;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 8px 12px;
    min-width: 250px;
    max-width: 400px;
    height: 54px; /* Fixed height for exactly 3 lines (18px per line) */
    font-size: 0.75rem;
    color: #6b7280;
    overflow-y: auto;
    overflow-x: hidden;
    
    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    
    &::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
    }
    
    ${breakpoint_small} {
        min-width: auto;
        max-width: 100%;
        width: 100%;
        height: 54px;
    }
`;

const LogEntry = styled.div`
    margin-bottom: 2px;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const CallBreakdownSectionWrapper = styled.div`
    width: 100vw;
    margin: 0 0 0 calc(-50vw + 50%);
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
    border-left: none;
    border-right: none;
    border-bottom: none;
    padding: 32px 0 32px 0;
    
    ${breakpoint_small} {
        padding: 16px 0 16px 0;
    }
    
    ${breakpoint_small_landscape} {
        padding: 24px 0 24px 0;
    }
`;

const CallBreakdownHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    padding: 0 32px;
    
    ${breakpoint_small} {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
        padding: 0 16px;
    }
    
    ${breakpoint_small_landscape} {
        padding: 0 20px;
    }
`;

const CallBreakdownControls = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    
    ${breakpoint_small} {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        width: 100%;
    }
    
    ${breakpoint_small_landscape} {
        flex-wrap: wrap;
        gap: 8px;
    }
`;

const GenerativeReportContainer = styled.div`
    padding-bottom: 24px;
    overflow: visible;
    
    @media (max-width: 768px) {
        padding-bottom: 16px;
    }
    
    @media (min-width: 1200px) {
        padding-bottom: 32px;
    }
`;

const FullWidthSectionWrapper = styled.div`
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    .card-body {
        padding: 0px;
    }
`;

const SessionTimestamp = styled.span`
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: normal;
`;

const PhoneNumberDisplay = styled.span`
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: normal;
    margin-left: 8px;
    font-family: monospace;
`;

const SessionRightSide = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const SessionControls = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 2px;
`;

const SessionIdDisplay = styled.div`
    font-size: 0.75rem;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    margin-left: 8px;
    margin-right: 8px;
`;

const CopiedIndicator = styled.span`
    font-size: 0.75rem;
    color: #10b981;
    font-weight: 500;
`;

const SmallSessionButton = styled(Button)`
    height: 24px;
    padding: 0 8px;
    font-size: 0.75rem;
    font-weight: normal;
    margin: 0;
    border-radius: 12px;
    
    .secondary-loader {
        margin-top: -5px;
    }
`;

const SmallCopyButton = styled(Button)`
    height: 24px;
    width: 24px;
    padding: 0;
    margin: 0;
    min-width: 24px;
    border: none;
    background: transparent;
    
    &:hover {
        background: transparent;
    }
    
    .secondary-loader {
        margin-top: -5px;
    }
`;

const ResetFiltersButton = styled(Button)`
    height: 32px;
    padding: 0 12px;
    font-size: 0.875rem;
    font-weight: 500;
    margin: 0;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    
    .secondary-loader {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        margin: 0;
        
        .spinner {
            margin: 0;
            height: 20px;
        }
    }
`;

const FilterModalButton = styled(Button)`
    padding: 8px 12px;
    font-size: 0.85rem;
       min-height: auto;
    height: fit-content;
    margin: 0;    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const LoadEngagementsButton = styled(Button)`
    padding: 8px 12px;
    font-size: 0.85rem;
    min-height: auto;
    height: 32px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ExportCSVButton = styled(Button)`
    padding: 8px 12px;
    font-size: 0.85rem;
    min-height: auto;
    height: 32px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const FilteredStatsDisplay = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    margin-right: 8px;
    padding: 8px 0;
    max-width: 800px;
    min-height: 34px;
    align-items: center;
    
    ${breakpoint_small} {
        flex-direction: column;
        align-items: flex-start;
        margin-right: 0;
        padding: 4px 0;
        gap: 4px;
        width: 100%;
    }
    
    ${breakpoint_small_landscape} {
        max-width: 100%;
        margin-right: 0;
    }
`;

const FilteredStatsText = styled.span`
    color: ${color_text_light};
    font-weight: 500;
    font-size: 0.875rem;
`;

const FilteredStatsTextContainer = styled.div`
    display: flex;
    align-items: center;
`;

const FilteredTagsContainer = styled.div`
    display: flex;
    gap: 4px;
    align-items: center;
    max-width: 600px;
    overflow: hidden;
    flex-shrink: 0;
    
    ${breakpoint_small} {
        display: none;
    }
    
    ${breakpoint_small_landscape} {
        display: none;
    }
`;

const FilteredTagPill = styled.span<{ tagColors?: { background: string; color: string; border: string; }; isExcluded?: boolean }>`
    display: inline-flex;
    align-items: center;
    background: ${props => {
        if (props.isExcluded) {
            return props.tagColors?.background || '#f3f4f6';
        }
        return props.tagColors?.background || '#f3f4f6';
    }};
    color: ${props => {
        if (props.isExcluded) {
            return props.tagColors?.color || '#6b7280';
        }
        return props.tagColors?.color || '#6b7280';
    }};
    border: ${props => props.isExcluded ? `1px solid ${props.tagColors?.border || '#d1d5db'}` : 'none'};
    font-size: 0.75rem;
    padding: 2px 6px;
    border-radius: 9999px;
    white-space: nowrap;
    height: 18px;
    font-style: normal;
    opacity: ${props => props.isExcluded ? '0.7' : '1'};
`;

const FilteredSearchPill = styled.span`
    display: inline-flex;
    align-items: center;
    background: ${color_shades_lighter};
    color: ${color_shades_darker};
    font-size: 0.75rem;
    padding: 2px 6px;
    border-radius: 9999px;
    white-space: nowrap;
    height: 18px;
    font-style: italic;
`;

const MoreFiltersIndicator = styled.span`
    display: inline-flex;
    align-items: center;
    background: ${color_shades_light};
    color: ${color_shades_darker};
    font-size: 0.75rem;
    padding: 2px 6px;
    border-radius: 9999px;
    white-space: nowrap;
    height: 18px;
    font-weight: 500;
    font-style: italic;
    cursor: pointer;
`;

const PaginationContainer = styled.div<{ isTop?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px 8px 24px;
    background: ${color_shades_lightest};
    
    ${breakpoint_small} {
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
    }
`;

const EmptyPaginationContainer = styled.div`
    height: 16px;
    background: ${color_shades_lightest};
`;

const PaginationInfo = styled.span`
    font-size: 0.875rem;
    color: ${color_text_light};
    font-weight: 500;
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PaginationButton = styled(Button)`
    min-width: 80px;
    height: 32px;
    font-size: 0.875rem;
    padding: 0 12px;
`;

const PageNumbers = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 0 8px;
    
    ${breakpoint_small} {
        margin: 0 4px;
    }
`;

const PageNumber = styled.button<{ active: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid ${props => props.active ? color_colors_ocean : color_shades_dark};
    background: ${props => props.active ? color_colors_ocean : color_defaults_white};
    color: ${props => props.active ? color_defaults_white : color_text_default};
    font-size: 0.875rem;
    font-weight: ${props => props.active ? '600' : '400'};
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.active ? color_colors_ocean : color_shades_lighter};
        border-color: ${props => props.active ? color_colors_ocean : color_shades_darker};
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 3px ${color_variants_ocean_light_1};
    }
`;

const PageEllipsis = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: ${color_shades_darker};
    font-size: 0.875rem;
    font-weight: 500;
`;

const SummaryLine = styled.div`
    white-space: pre-wrap;
`;

const SessionIdList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0;
`;

type CountStats = {
    key: string;
    count: number;
    percentage: string; // formatted as "0.25"
};

interface GenerativeTableProps {
    applicationId: string
    filter: AnalyticsRequestFilter
    application: ApplicationModel
    sessionContainer?: any
    onProcessingChange?: (isProcessing: boolean) => void
    crossOrgMode?: boolean
    currentOrgMode?: boolean
    orgIds?: string[]
    appIds?: string[]
    applications?: ApplicationModel[]
    orderPlacedEvents?: any[],
    setUniqueEngagementCount?: (count: number) => void;
    setConvertedCustomers?: (count: number) => void;
    setUpsellPercentage?: (percentage: number) => void;
    setRepeatCustomers?: (count: number) => void;
    onFilteredDataChange?: (filteredReviews: SessionReview[], selectedTags?: string[], excludedTags?: string[]) => void;
};

const GenerativeReportTable: React.FC<GenerativeTableProps> = (props) => {
    const { getTagName, getTagColors, sortTagsByPriority, allTags, loadTagConfig } = useTagConfig();
    const [sessionReviews, setSessionReviews] = useState<SessionReview[]>([]);
    const [processingReviews, setProcessingReviews] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [excludedTags, setExcludedTags] = useState<string[]>([]);
    const [searchFieldValue, setSearchFieldValue] = useState("");
    const [visibleSessionIds, setVisibleSessionIds] = useState<string[]>([]);
    const [copiedSessionId, setCopiedSessionId] = useState<string | null>(null);
    const [collapsedCards, setCollapsedCards] = useState<string[]>([]);
    const [featureFlag, setFeatureFlag] = useState<string | undefined>(undefined);
    const [crossOrgSessions, setCrossOrgSessions] = useState<any[]>([]);
    const [loadingLog, setLoadingLog] = useState<string[]>([]);
    const [debouncedSelectedTags, setDebouncedSelectedTags] = useState<string[]>([]);
    const [debouncedExcludedTags, setDebouncedExcludedTags] = useState<string[]>([]);
    const [debouncedSearchFieldValue, setDebouncedSearchFieldValue] = useState("");
    const [resettingFilters, setResettingFilters] = useState(false);
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
    const [categoryCurrentPages, setCategoryCurrentPages] = useState<Record<string, number>>({});
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [hasLoadedCrossOrgData, setHasLoadedCrossOrgData] = useState(false);
    const [filterLogic, setFilterLogic] = useState<'AND' | 'OR'>('AND');
    const [lastLoadTime, setLastLoadTime] = useState<Date | null>(null);

    const loadingLogRef = useRef<HTMLDivElement>(null);
    const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const selectedTagsSet = useMemo(() => new Set(selectedTags), [selectedTags]);
    const excludedTagsSet = useMemo(() => new Set(excludedTags), [excludedTags]);

    const sessionContainer = props.sessionContainer || SessionAnalyticsContainer.useContainer();

    const memoizedFilter = useMemo(() => props.filter, [JSON.stringify(props.filter)]);

    // Helper function to get application name by ID
    const getAppNameById = useCallback((appId: string): string => {
        if (props.applications) {
            const app = props.applications.find(a => a.id === appId);
            return app?.name || appId;
        }
        return appId;
    }, [props.applications]);

    const availableCategories = React.useMemo(() => {
        const desiredOrder = [
            'ACTION_ATTEMPTED',
            'OTHER',
            'IMMEDIATE_TRANSFER',
            'NO_INTERACTION'
        ];
        const uniqueCategories = Array.from(new Set(sessionReviews.map(r => r.category)));
        return desiredOrder.filter(cat => uniqueCategories.includes(cat)).concat(
            uniqueCategories.filter(cat => !desiredOrder.includes(cat))
        );
    }, [sessionReviews]);

    // Optimized: Combine tag computation and use Set for faster lookups
    const { availableTags, tagsForDisplay } = useMemo(() => {
        const tagSet = new Set<string>([...allTags]);
        let hasSessionsWithoutTags = false;

        // Single pass through session reviews
        for (const review of sessionReviews) {
            if (review.tags && review.tags.length > 0) {
                for (const tag of review.tags) {
                    tagSet.add(tag);
                }
            } else {
                hasSessionsWithoutTags = true;
            }
        }

        const tags = Array.from(tagSet);
        let sortedTags = sortTagsByPriority(tags, featureFlag);
        sortedTags = filterInternalVoicifyTags(
            sortedTags,
            !!props.crossOrgMode || canViewInternalVoicifyTags()
        );

        if (hasSessionsWithoutTags && !sortedTags.includes('NO_TAGS_AVAILABLE')) {
            sortedTags.push('NO_TAGS_AVAILABLE');
        }

        // Compute display names in the same memo
        const displayNames = sortedTags.map(tag =>
            tag === 'NO_TAGS_AVAILABLE' ? 'Untagged' : getTagName(tag, featureFlag) || tag
        );

        return {
            availableTags: sortedTags,
            tagsForDisplay: displayNames
        };
    }, [sessionReviews, featureFlag, allTags, sortTagsByPriority, getTagName, props.crossOrgMode]);

    const getFeatureFlag = (): string | undefined => {
        if (props.application?.featureFlags?.some(flag => flag.id === FoodOrderingFeatureFlagId)) {
            return "FOOD_ORDERING";
        } else if (props.application?.featureFlags?.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)) {
            return "HEALTHCARE";
        } else {
            return undefined;
        }
    };

    useEffect(() => {
        const flag = getFeatureFlag();
        setFeatureFlag(flag);
    }, [props.applicationId, props.application?.featureFlags]);

    useEffect(() => {
        if (props.applicationId) {
            loadTagConfig(props.applicationId);
        }
    }, [props.applicationId]);

    useEffect(() => {
        if (!props.crossOrgMode && !props.currentOrgMode) {
            getGenerativeSessionReviews();
        }
    }, [props.applicationId, props.crossOrgMode, props.currentOrgMode, props.orgIds, props.appIds, memoizedFilter]);

    // Reset cross-org/current-org data loaded state when filter changes
    useEffect(() => {
        if (props.crossOrgMode || props.currentOrgMode) {
            setHasLoadedCrossOrgData(false);
        }
    }, [props.applicationId, props.crossOrgMode, props.currentOrgMode, props.orgIds, props.appIds, memoizedFilter]);

    // Auto-refresh logic for cross-org mode or current org mode when end date is after now
    useEffect(() => {
        // Clear any existing interval
        if (autoRefreshIntervalRef.current) {
            clearInterval(autoRefreshIntervalRef.current);
            autoRefreshIntervalRef.current = null;
        }

        // Only set up auto-refresh if:
        // 1. We're in cross-org mode or current org mode
        // 2. We've loaded data at least once
        // 3. The end date is after the current time
        const isMultiMode = props.crossOrgMode || props.currentOrgMode;
        if (isMultiMode && hasLoadedCrossOrgData && lastLoadTime) {
            const endDate = memoizedFilter.endDate ? moment(memoizedFilter.endDate) : null;
            const now = moment();

            if (endDate && endDate.isAfter(now)) {
                // Set up interval to check for new sessions every 5 minutes
                autoRefreshIntervalRef.current = setInterval(() => {
                    loadNewSessions();
                }, AUTO_REFRESH_INTERVAL_MINUTES * 60 * 1000); // Convert minutes to milliseconds
            }
        }

        // Cleanup on unmount or when dependencies change
        return () => {
            if (autoRefreshIntervalRef.current) {
                clearInterval(autoRefreshIntervalRef.current);
                autoRefreshIntervalRef.current = null;
            }
        };
    }, [props.crossOrgMode, props.currentOrgMode, hasLoadedCrossOrgData, lastLoadTime, memoizedFilter.endDate]);

    useEffect(() => {
        // Sync local processing state with session container loading state only in single app mode
        // Only sync if we have initially loaded data (to prevent initial flash)
        const isMultiMode = props.crossOrgMode || props.currentOrgMode;
        if (!isMultiMode && hasInitiallyLoaded) {
            setProcessingReviews(sessionContainer.loading);
        }
    }, [sessionContainer.loading, props.crossOrgMode, props.currentOrgMode, hasInitiallyLoaded]);

    useEffect(() => {
        if (props.onProcessingChange) {
            props.onProcessingChange(processingReviews);
        }
    }, [processingReviews, props.onProcessingChange]);

    // Auto-scroll loading log to bottom when new logs are added
    useEffect(() => {
        if (loadingLogRef.current) {
            loadingLogRef.current.scrollTop = loadingLogRef.current.scrollHeight;
        }
    }, [loadingLog]);

    // Memoize phone number lookup for better performance
    const sessionPhoneNumbers = useMemo(() => {
        const phoneMap = new Map<string, string>();
        const isMultiMode = props.crossOrgMode || props.currentOrgMode;
        const sessions = isMultiMode ? crossOrgSessions : sessionContainer.sessions;

        for (const session of sessions) {
            if (session.sessionId && session?.from) {
                phoneMap.set(session.sessionId, session?.from);
            }
        }

        return phoneMap;
    }, [props.crossOrgMode, props.currentOrgMode, crossOrgSessions, sessionContainer.sessions]);

    // Debounce filter changes to improve performance with large datasets
    useEffect(() => {
        // Clear existing debounce timer
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }
        // Reduced debounce delay for better responsiveness
        filterDebounceRef.current = setTimeout(() => {
            setDebouncedSelectedTags(selectedTags);
            setDebouncedExcludedTags(excludedTags);
            setDebouncedSearchFieldValue(searchFieldValue);
        }, 500);

        // Cleanup function to clear timer on unmount or dependency change
        return () => {
            if (filterDebounceRef.current) {
                clearTimeout(filterDebounceRef.current);
            }
        };
    }, [selectedTags, excludedTags, searchFieldValue]);

    // Initialize debounced values on first load
    useEffect(() => {
        setDebouncedSelectedTags(selectedTags);
        setDebouncedExcludedTags(excludedTags);
        setDebouncedSearchFieldValue(searchFieldValue);
    }, [sessionReviews]); // Only run when sessionReviews changes (initial load)

    // Reset pagination when filters change
    useEffect(() => {
        setCategoryCurrentPages({});
    }, [debouncedSelectedTags, debouncedExcludedTags, debouncedSearchFieldValue]);

    const stop = (e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation();

    const toggleSessionIdVisibility = (sessionId: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setVisibleSessionIds(prev => {
            if (prev.includes(sessionId)) {
                return prev.filter(id => id !== sessionId);
            } else {
                return [...prev, sessionId];
            }
        });
    };

    const copySessionId = (sessionId: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(sessionId);
        setCopiedSessionId(sessionId);
        setTimeout(() => {
            setCopiedSessionId(null);
        }, 2000);
    };

    const toggleCardCollapse = (sessionId: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setCollapsedCards(prev => {
            if (prev.includes(sessionId)) {
                return prev.filter(id => id !== sessionId);
            } else {
                return [...prev, sessionId];
            }
        });
    };

    // Memoize session timestamps for better performance
    const sessionTimestamps = useMemo(() => {
        const timestampMap = new Map<string, string>();
        const isMultiMode = props.crossOrgMode || props.currentOrgMode;
        const sessions = isMultiMode ? crossOrgSessions : sessionContainer.sessions;

        for (const session of sessions) {
            if (session?.sessionId && session?.startDate) {
                timestampMap.set(session?.sessionId, moment(session?.startDate).format(SESSION_DATE_FORMAT));
            }
        }

        return timestampMap;
    }, [props.crossOrgMode, props.currentOrgMode, crossOrgSessions, sessionContainer.sessions]);

    const getSessionTimeStamp = useCallback((review: SessionReview) => {
        return sessionTimestamps.get(review.sessionId) || 'N/A';
    }, [sessionTimestamps]);

    const getSessionPhoneNumber = useCallback((review: SessionReview) => {
        return sessionPhoneNumbers.get(review.sessionId);
    }, [sessionPhoneNumbers]);

    const formatLastFourDigits = (phoneNumber: string) => {
        if (!phoneNumber) return null;
        // Remove any non-digit characters from the input number
        const cleanedNumber = phoneNumber.replace(/\D/g, '');
        // Return last 4 digits if the number is long enough
        if (cleanedNumber.length >= 4) {
            return cleanedNumber.substring(cleanedNumber.length - 4);
        }
        return cleanedNumber;
    };

    const formatSummaryText = (text: string) => {
        if (!text) return text;

        const trimmedText = text.trim();
        if (!trimmedText) return trimmedText;

        const lines = trimmedText.split('\n');
        const formattedLines = lines.map(line => {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('- ')) {
                // Replace "- " with bullet point and return as a list item
                return `• ${trimmedLine.substring(2)}`;
            }
            if (trimmedLine.startsWith('* ')) {
                // Replace "* " with bullet point and return as a list item
                return `• ${trimmedLine.substring(2)}`;
            }
            return trimmedLine;
        });

        return formattedLines.join('\n').trim();
    };

    const getIconByTagLabel = (tag: string) => {
        const mappedTagName = getTagName(tag, featureFlag);

        switch (mappedTagName) {
            case 'Unsupported Request':
            case 'AI Issue':
                return assistantIssueIcon;
            case 'Transferred':
                return transferredIcon;
            case 'Order Placed':
            case 'Appointment Scheduled':
                return orderPlacedIcon;
            default:
                return null;
        }
    };

    const toggleTag = useCallback((tag: string, mode: 'include' | 'exclude' = 'include') => {
        if (mode === 'include') {
            if (excludedTags.includes(tag)) {
                // If it's currently excluded and we're toggling to include, just remove from excluded
                setExcludedTags((prev) => prev.filter(t => t !== tag));
                return;
            }

            setSelectedTags((prev) => {
                if (prev.includes(tag)) {
                    return prev.filter(t => t !== tag);
                } else {
                    return [...prev, tag];
                }
            });
        } else {
            if (selectedTags.includes(tag)) {
                // If it's currently included and we're toggling to exclude, remove from included first
                setSelectedTags((prev) => prev.filter(t => t !== tag));
            }

            setExcludedTags((prev) => {
                if (prev.includes(tag)) {
                    return prev.filter(t => t !== tag);
                } else {
                    return [...prev, tag];
                }
            });
        }
    }, [selectedTags, excludedTags]);

    const handleFilterModalOpen = useCallback(() => {
        setIsFilterModalOpen(true);
    }, []);

    const handleFilterModalClose = useCallback(() => {
        setIsFilterModalOpen(false);
        setIsApplyingFilters(false);
    }, []);

    const handleFilterModalApply = useCallback(() => {
        setIsApplyingFilters(true);
        // The actual filtering logic is handled by the debounced effects
        // This just triggers the loading state and closes the modal
        setTimeout(() => {
            setIsApplyingFilters(false);
        }, 500); // Brief delay to show loading state
    }, []);

    const [engagements, setEngagements] = useState<OrganizationEngagement[]>([]);

    /**
     * Loads generative session reviews from engagement endpoints.
     * In cross-org mode, loads data from multiple organizations specified in orgIds prop.
     * In current-org mode, loads data for each application individually.
     * In single-app mode, loads data for the current application.
     */
    const getGenerativeSessionReviews = useCallback(async () => {
        setProcessingReviews(true);

        // Clear loading log only on initial load for cross-org mode or current org mode
        const isMultiMode = props.crossOrgMode || props.currentOrgMode;
        if (isMultiMode && loadingLog.length === 0) {
            setLoadingLog(['Starting data load...']);
        } else if (props.crossOrgMode && props.orgIds) {
            setLoadingLog(prev => [...prev, `Starting data load for ${props.orgIds!.length} organizations...`]);
        } else if (props.currentOrgMode && props.appIds) {
            setLoadingLog(prev => [...prev, `Starting data load for ${props.appIds!.length} applications...`]);
        }

        // Create a logging callback
        const addToLog = (message: string) => {
            if (isMultiMode) {
                setLoadingLog(prev => [...prev, message]);
            }
        };

        try {
            let allEngagements: OrganizationEngagement[] = [];
            let crossOrgSessionsData: any[] = [];

            const engagementFilter = {
                startDate: memoizedFilter.startDate,
                endDate: memoizedFilter.endDate,
                timezone: memoizedFilter.timezone
            };

            if (props.crossOrgMode && props.orgIds && props.orgIds.length > 0) {
                // Cross-org mode: Load engagements from multiple organizations
                for (let i = 0; i < props.orgIds.length; i++) {
                    const orgId = props.orgIds[i];
                    addToLog(`Loading engagements for org ${i + 1} of ${props.orgIds.length}...`);
                    try {
                        const orgEngagements = await loadEngagementsPaged(
                            getOrganizationEngagements,
                            orgId,
                            engagementFilter,
                            5000,
                            200000,
                            addToLog
                        );
                        allEngagements.push(...orgEngagements);
                        addToLog(`✓ Loaded ${orgEngagements.length} engagements (Total so far: ${allEngagements.length})`);
                    } catch (error) {
                        addToLog(`✗ Exception loading engagements for org ${orgId}: ${error.message || error}`);
                    }
                }

                setLoadingLog(prev => [...prev, `Loaded ${allEngagements.length} engagements across all organizations`]);

                // Also load sessions for these organizations so we can get timestamps and phone numbers
                crossOrgSessionsData = await sessionContainer.loadSessionsForOrgs(
                    props.orgIds,
                    memoizedFilter,
                    addToLog
                );
                setCrossOrgSessions(crossOrgSessionsData || []);

                setLoadingLog(prev => [...prev, `Loaded ${(crossOrgSessionsData || []).length} sessions across all organizations`]);
            } else if (props.currentOrgMode && props.appIds && props.appIds.length > 0) {
                // Current org mode: Load engagements for each application individually
                setLoadingLog(prev => [...prev, `Loading data for ${props.appIds.length} applications...`]);

                for (let i = 0; i < props.appIds.length; i++) {
                    const appId = props.appIds[i];
                    const appName = getAppNameById(appId);
                    addToLog(`Loading engagements for ${appName} (${i + 1} of ${props.appIds.length})...`);
                    try {
                        const appEngagements = await loadEngagementsPaged(
                            getApplicationEngagements,
                            appId,
                            engagementFilter,
                            5000,
                            200000,
                            addToLog
                        );
                        allEngagements.push(...appEngagements);
                        addToLog(`✓ Loaded ${appEngagements.length} engagements for ${appName} (Total so far: ${allEngagements.length})`);
                    } catch (error) {
                        addToLog(`✗ Exception loading engagements for ${appName}: ${error.message || error}`);
                    }
                }

                setLoadingLog(prev => [...prev, `Loaded ${allEngagements.length} engagements across all applications`]);

                // Load sessions for each application for timestamps and phone numbers
                for (let i = 0; i < props.appIds.length; i++) {
                    const appId = props.appIds[i];
                    const appName = getAppNameById(appId);
                    addToLog(`Loading sessions for ${appName} (${i + 1} of ${props.appIds.length})...`);
                    try {
                        const sessionsResult = await voicifyApi.getSessionsByApplication(appId, memoizedFilter);
                        const callStartedResult = await voicifyApi.getCallStartedEventsByApplication(appId, memoizedFilter);

                        if (sessionsResult.resultType === "Ok" && callStartedResult.resultType === "Ok") {
                            const sessionModels = sessionsResult.data || [];
                            const callStartedEvents = callStartedResult.data || [];

                            addToLog(`  → Received ${sessionModels.length} session records`);
                            addToLog(`  → Received ${callStartedEvents.length} call started events`);

                            // Convert to SessionListModel format
                            for (const session of sessionModels) {
                                const callStarted = callStartedEvents.find(e => e.sessionId === session.sessionId);
                                const phoneNumbers = [];
                                if (callStarted?.from?.length) phoneNumbers.push(callStarted.from);
                                if (callStarted?.to?.length) phoneNumbers.push(callStarted.to);
                                if (callStarted?.phoneNumber?.length) phoneNumbers.push(callStarted.phoneNumber);

                                crossOrgSessionsData.push({
                                    sessionId: session.sessionId,
                                    startDate: new Date(session.startDate),
                                    phoneNumbers: phoneNumbers,
                                    from: callStarted?.from
                                });
                            }
                            addToLog(`✓ Loaded ${sessionModels.length} sessions for ${appName} (Total so far: ${crossOrgSessionsData.length})`);
                        } else {
                            const errorMsg = sessionsResult.resultType !== "Ok"
                                ? `Sessions: ${sessionsResult?.errors?.[0] || 'Unknown error'}`
                                : `Call events: ${callStartedResult?.errors?.[0] || 'Unknown error'}`;
                            addToLog(`✗ Error loading sessions for ${appName}: ${errorMsg}`);
                        }
                    } catch (error) {
                        addToLog(`✗ Exception loading sessions for ${appName}: ${error.message || error}`);
                    }
                }

                setCrossOrgSessions(crossOrgSessionsData || []);
                setLoadingLog(prev => [...prev, `Loaded ${(crossOrgSessionsData || []).length} sessions across all applications`]);
            } else {
                // Single application mode: Load engagements for the current application
                allEngagements = await loadEngagementsPaged(
                    getApplicationEngagements,
                    props.applicationId,
                    engagementFilter
                );
                // Clear cross-org sessions when not in cross-org mode
                setCrossOrgSessions([]);
            }

            // Store raw engagements for stats computation
            setEngagements(allEngagements);

            // Map engagements to session reviews and set them directly
            const isCrossOrg = props.crossOrgMode;
            const reviews = mapEngagementsToSessionReviews(allEngagements, isCrossOrg);
            setSessionReviews(reviews);

            // Compute and set stats
            const stats = computeEngagementStats(allEngagements);
            if (props.setUniqueEngagementCount) props.setUniqueEngagementCount(stats.uniqueEngagementCount);
            if (props.setConvertedCustomers) props.setConvertedCustomers(stats.convertedCustomers);
            if (props.setRepeatCustomers) props.setRepeatCustomers(stats.repeatCustomers);
            if (props.setUpsellPercentage) props.setUpsellPercentage(stats.upsellPercentage);

            if (isMultiMode) {
                setLoadingLog(prev => [...prev, `Processed ${allEngagements.length} engagements into ${reviews.length} session reviews`]);
            }

        } catch (error) {
            console.error('Error loading generative session reviews:', error);
            setSessionReviews([]);
            setEngagements([]);
            setCrossOrgSessions([]);

            if (isMultiMode) {
                setLoadingLog(prev => [...prev, `Error loading data: ${error}`]);
            }
        } finally {
            setProcessingReviews(false);
            setHasInitiallyLoaded(true);

            if (isMultiMode) {
                setLoadingLog(prev => [...prev, 'Data loading completed']);
            }

            // Track the load time for auto-refresh
            setLastLoadTime(new Date());
        }
    }, [props.crossOrgMode, props.currentOrgMode, props.orgIds, props.appIds, props.applicationId, memoizedFilter, sessionContainer, loadingLog.length]);

    /**
     * Loads new engagements since the last load time.
     * Only used in cross-org mode or current org mode when end date is after the current time.
     */
    const loadNewSessions = useCallback(async () => {
        const hasOrgIds = props.crossOrgMode && props.orgIds && props.orgIds.length > 0;
        const hasAppIds = props.currentOrgMode && props.appIds && props.appIds.length > 0;

        if ((!hasOrgIds && !hasAppIds) || !lastLoadTime) {
            return;
        }

        console.log('Checking for new engagements since:', lastLoadTime);

        const addToLog = (message: string) => {
            setLoadingLog(prev => [...prev, message]);
        };

        try {
            // Create a modified filter using lastLoadTime as start date and keep same end date
            const modifiedFilter = {
                startDate: lastLoadTime.toISOString(),
                endDate: memoizedFilter.endDate,
                timezone: memoizedFilter.timezone
            };

            addToLog(`Checking for new engagements since ${moment(lastLoadTime).format(SESSION_DATE_FORMAT)}...`);

            let newEngagements: OrganizationEngagement[] = [];
            let newSessionsData: any[] = [];

            if (props.crossOrgMode && props.orgIds) {
                // Cross-org mode: Load new engagements for multiple organizations
                for (const orgId of props.orgIds) {
                    const orgEngagements = await loadEngagementsPaged(
                        getOrganizationEngagements,
                        orgId,
                        modifiedFilter,
                        5000,
                        200000,
                        addToLog
                    );
                    newEngagements.push(...orgEngagements);
                }

                if (newEngagements.length > 0) {
                    addToLog(`Found ${newEngagements.length} new engagements`);

                    // Load new sessions for timestamps and phone numbers
                    newSessionsData = await sessionContainer.loadSessionsForOrgs(
                        props.orgIds,
                        { ...memoizedFilter, startDate: lastLoadTime.toISOString() },
                        addToLog
                    ) || [];
                }
            } else if (props.currentOrgMode && props.appIds) {
                // Current org mode: Load new engagements for each application
                for (const appId of props.appIds) {
                    const appEngagements = await loadEngagementsPaged(
                        getApplicationEngagements,
                        appId,
                        modifiedFilter,
                        5000,
                        200000,
                        addToLog
                    );
                    newEngagements.push(...appEngagements);

                    const sessionsResult = await voicifyApi.getSessionsByApplication(appId, { ...memoizedFilter, startDate: lastLoadTime.toISOString() });
                    const callStartedResult = await voicifyApi.getCallStartedEventsByApplication(appId, { ...memoizedFilter, startDate: lastLoadTime.toISOString() });

                    if (sessionsResult.resultType === "Ok" && callStartedResult.resultType === "Ok") {
                        const sessionModels = sessionsResult.data || [];
                        const callStartedEvents = callStartedResult.data || [];

                        for (const session of sessionModels) {
                            const callStarted = callStartedEvents.find(e => e.sessionId === session.sessionId);
                            const phoneNumbers = [];
                            if (callStarted?.from?.length) phoneNumbers.push(callStarted.from);
                            if (callStarted?.to?.length) phoneNumbers.push(callStarted.to);
                            if (callStarted?.phoneNumber?.length) phoneNumbers.push(callStarted.phoneNumber);

                            newSessionsData.push({
                                sessionId: session.sessionId,
                                startDate: new Date(session.startDate),
                                phoneNumbers: phoneNumbers,
                                from: callStarted?.from
                            });
                        }
                    }
                }

                if (newEngagements.length > 0) {
                    addToLog(`Found ${newEngagements.length} new engagements across applications`);
                }
            }

            if (newEngagements.length > 0) {
                if (newSessionsData.length > 0) {
                    addToLog(`Found ${newSessionsData.length} new sessions`);
                }

                // Merge new engagements with existing ones
                setEngagements(prev => {
                    const merged = [...prev, ...newEngagements];
                    // Recompute stats with merged engagements
                    const stats = computeEngagementStats(merged);
                    if (props.setUniqueEngagementCount) props.setUniqueEngagementCount(stats.uniqueEngagementCount);
                    if (props.setConvertedCustomers) props.setConvertedCustomers(stats.convertedCustomers);
                    if (props.setRepeatCustomers) props.setRepeatCustomers(stats.repeatCustomers);
                    if (props.setUpsellPercentage) props.setUpsellPercentage(stats.upsellPercentage);
                    return merged;
                });

                // Map new engagements to session reviews and merge
                const isCrossOrg = props.crossOrgMode;
                const newReviews = mapEngagementsToSessionReviews(newEngagements, isCrossOrg);
                setSessionReviews(prev => [...prev, ...newReviews]);
                setCrossOrgSessions(prev => [...prev, ...newSessionsData]);

                addToLog('New engagements merged successfully');

                // Update last load time
                setLastLoadTime(new Date());
            } else {
                addToLog('No new engagements found');
            }
        } catch (error) {
            console.error('Error loading new engagements:', error);
            addToLog(`Error loading new engagements: ${error}`);
        }
    }, [props.crossOrgMode, props.currentOrgMode, props.orgIds, props.appIds, lastLoadTime, memoizedFilter, sessionContainer]);

    const handleLoadEngagements = useCallback(() => {
        if (props.crossOrgMode || props.currentOrgMode) {
            setHasLoadedCrossOrgData(true);
            getGenerativeSessionReviews();
        }
    }, [props.crossOrgMode, props.currentOrgMode, getGenerativeSessionReviews]);

    // Check if we can load engagements (have necessary data)
    const canLoadEngagements = useMemo(() => {
        if (!props.crossOrgMode && !props.currentOrgMode) return false;
        if (processingReviews) return false;
        if (!memoizedFilter?.startDate || !memoizedFilter?.endDate) return false;

        if (props.crossOrgMode && (!props.orgIds || props.orgIds.length === 0)) return false;
        if (props.currentOrgMode && (!props.appIds || props.appIds.length === 0)) return false;

        return true;
    }, [props.crossOrgMode, props.currentOrgMode, props.orgIds, props.appIds, processingReviews, memoizedFilter]);

    const getSessionStatistics = (sessionReviews: SessionReview[]) => {
        const categoryCounts = new Map<string, number>();
        const tagCounts = new Map<string, number>();
        let total = 0;

        for (const review of sessionReviews) {
            total += 1;

            categoryCounts.set(
                review.category,
                (categoryCounts.get(review.category) ?? 0) + 1
            );

            if (review.tags && review.tags.length > 0) {
                for (const tag of review.tags) {
                    tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
                }
            } else {
                tagCounts.set('NO_TAGS_AVAILABLE', (tagCounts.get('NO_TAGS_AVAILABLE') ?? 0) + 1);
            }
        }

        const categories: CountStats[] = Array.from(categoryCounts.entries()).map(
            ([key, count]) => ({
                key,
                count,
                percentage: (count / total).toFixed(2),
            })
        );

        const tags: CountStats[] = Array.from(tagCounts.entries()).map(
            ([key, count]) => ({
                key,
                count,
                percentage: (count / total).toFixed(2),
            })
        );

        return { categories, tags };
    };

    const sessionStatistics = React.useMemo(() => getSessionStatistics(sessionReviews), [sessionReviews]);

    // Optimized filtering with memoized phone numbers and early exits
    const getFilteredReviews = useCallback((reviews: SessionReview[], category?: string) => {
        return reviews.filter((review) => {
            if (category && review.category !== category) return false;

            // Search filter with optimized phone number lookup and application name
            if (debouncedSearchFieldValue?.length) {
                const phoneNumber = sessionPhoneNumbers.get(review.sessionId);
                const lastFourDigits = phoneNumber ? formatLastFourDigits(phoneNumber) : null;
                const appName = getAppNameById(review.applicationId);
                const searchLower = debouncedSearchFieldValue.toLowerCase();

                let matchesSearch = review.sessionId.includes(debouncedSearchFieldValue) ||
                    (lastFourDigits && lastFourDigits.includes(debouncedSearchFieldValue)) ||
                    (appName && appName.toLowerCase().includes(searchLower));

                if (!matchesSearch) {
                    if (review?.mergedSessions?.some(s => s.sessionId.includes(debouncedSearchFieldValue))) {
                        matchesSearch = true;
                    }
                }

                if (!matchesSearch) return false;
            }

            // Tag filtering with early exits
            const hasIncludeFilters = debouncedSelectedTags.length > 0;
            const hasExcludeFilters = debouncedExcludedTags.length > 0;

            if (!hasIncludeFilters && !hasExcludeFilters) return true;

            const sessionHasTags = review.tags && review.tags.length > 0;


            if (filterLogic === "OR") {
                //Handle include filters first - early exit if we have any of them
                let includeTrue = false;
                let excludeTrue = false;
                if (hasIncludeFilters) {
                    if (sessionHasTags) {
                        // Use some() for early exit when included tag is found
                        const hasIncludeTag = debouncedSelectedTags.some((includeTag) =>
                            review.tags!.includes(includeTag)
                        );
                        includeTrue = hasIncludeTag;
                    } else {
                        // Session has no tags, check if "NO_TAGS_AVAILABLE" is included
                        includeTrue = debouncedSelectedTags.includes('NO_TAGS_AVAILABLE');
                    }
                }
                if (hasExcludeFilters) {
                    if (sessionHasTags) {
                        //If session has ALL excluded tags it should be added, otherwise it should be
                        const hasAllExcludeTags = debouncedExcludedTags.every((t) => review.tags!.includes(t));
                        excludeTrue = !hasAllExcludeTags;
                    } else {
                        //If the session has no tags return true if NO tags available is not excluded
                        excludeTrue = !debouncedExcludedTags.includes('NO_TAGS_AVAILABLE')
                    }
                }
                return includeTrue || excludeTrue;
            } else {
                // Handle exclude filters first - early exit if excluded
                if (hasExcludeFilters) {
                    if (sessionHasTags) {
                        // Use some() for early exit when excluded tag is found
                        const hasExcludedTag = debouncedExcludedTags.some((excludedTag) =>
                            review.tags!.includes(excludedTag)
                        );
                        if (hasExcludedTag) return false;
                    } else {
                        // Session has no tags, check if "NO_TAGS_AVAILABLE" is excluded
                        if (debouncedExcludedTags.includes('NO_TAGS_AVAILABLE')) return false;
                    }
                }

                // Handle include filters
                if (hasIncludeFilters) {
                    if (sessionHasTags) {
                        return debouncedSelectedTags.every((t) => review.tags!.includes(t));
                    } else {
                        return debouncedSelectedTags.includes('NO_TAGS_AVAILABLE');
                    }
                }

                return true;
            }
        });
    }, [debouncedSelectedTags, debouncedExcludedTags, debouncedSearchFieldValue, sessionPhoneNumbers, getAppNameById]);

    // Memoize filtered reviews to avoid redundant calculations
    const filteredReviewsCache = useMemo(() => {
        const cache = new Map<string, SessionReview[]>();

        // Cache filtered reviews for each category
        for (const category of availableCategories) {
            const key = `${category}`;
            cache.set(key, getFilteredReviews(sessionReviews, category));
        }

        // Cache uncategorized reviews
        cache.set('uncategorized', getFilteredReviews(sessionReviews));

        return cache;
    }, [sessionReviews, availableCategories, getFilteredReviews]);

    // Helper function to get cached filtered reviews
    const getCachedFilteredReviews = useCallback((category?: string) => {
        const key = category || 'uncategorized';
        return filteredReviewsCache.get(key) || [];
    }, [filteredReviewsCache]);

    const handleExportCSV = useCallback(() => {
        // Get all filtered reviews
        const allFilteredReviews = getCachedFilteredReviews();

        if (allFilteredReviews.length === 0) {
            alert('No data to export');
            return;
        }

        // Helper function to escape CSV values and prevent Excel formula injection
        const escapeCsvValue = (value: string): string => {
            if (!value) return '""';

            // Remove leading/trailing whitespace
            let escaped = value.trim();

            // Replace double quotes with double-double quotes for CSV
            escaped = escaped.replace(/"/g, '""');

            // Replace newlines and carriage returns with spaces
            escaped = escaped.replace(/[\r\n]+/g, ' ');

            // Prevent Excel formula injection by prefixing dangerous characters with a single quote
            // This makes Excel treat it as text rather than a formula
            if (escaped.match(/^[=+\-@\t\r]/)) {
                escaped = "'" + escaped;
            }

            return `"${escaped}"`;
        };

        // Prepare CSV headers
        const headers = ['Date Time', 'Application Name', 'Session ID', 'Category', 'Tags', 'Number of Calls', 'Summary', 'Application ID'];

        // Prepare CSV rows
        const rows = allFilteredReviews.map(review => {
            const appName = getAppNameById(review.applicationId || '');
            const tags = review.tags && review.tags.length > 0 ? review.tags.join('; ') : '';
            const numberOfCalls = review.mergedSessions ? review.mergedSessions.length : 1;
            const dateTime = review.date ? moment(review.date).format('YYYY-MM-DD HH:mm:ss') : getSessionTimeStamp(review);

            return [
                escapeCsvValue(dateTime),
                escapeCsvValue(appName),
                escapeCsvValue(review.sessionId || ''),
                escapeCsvValue(review.category || ''),
                escapeCsvValue(tags),
                escapeCsvValue(numberOfCalls.toString()),
                escapeCsvValue(review.summary || ''),
                escapeCsvValue(review.applicationId || ''),
            ];
        });

        // Create CSV content
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
        link.setAttribute('href', url);
        link.setAttribute('download', `engagements_export_${timestamp}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [getCachedFilteredReviews, getAppNameById, getSessionTimeStamp]);

    const getFilteredStatistics = useCallback(() => {
        const hasActiveFilters = debouncedSelectedTags.length > 0 || debouncedExcludedTags.length > 0 || debouncedSearchFieldValue?.length > 0;
        if (!hasActiveFilters) return null;

        const totalFilteredReviews = getCachedFilteredReviews();
        const totalCount = sessionReviews.length;
        const filteredCount = totalFilteredReviews.length;
        const percentage = totalCount > 0 ? (filteredCount / totalCount * 100).toFixed(0) : '0';

        return {
            count: filteredCount,
            percentage: percentage,
            hasActiveFilters: hasActiveFilters
        };
    }, [sessionReviews, debouncedSelectedTags, debouncedExcludedTags, debouncedSearchFieldValue, getCachedFilteredReviews]);

    const filteredStatistics = useMemo(() => getFilteredStatistics(), [getFilteredStatistics]);

    useEffect(() => {
        if (props.onFilteredDataChange) {
            const allFilteredReviews = getCachedFilteredReviews();
            props.onFilteredDataChange(allFilteredReviews, debouncedSelectedTags, debouncedExcludedTags);
        }
    }, [getCachedFilteredReviews, props.onFilteredDataChange, debouncedSelectedTags, debouncedExcludedTags]);

    const renderHiddenFiltersTooltip = (hiddenTags: Array<{ tag: string; mode: 'include' | 'exclude' | 'search' }>) => {
        const hiddenFiltersText = hiddenTags.map(({ tag, mode }) => {
            if (mode === 'search') {
                return tag;
            }
            const displayName = tag === 'NO_TAGS_AVAILABLE' ? 'Untagged' : getTagName(tag, featureFlag);
            return mode === 'exclude' ? `NOT ${displayName}` : displayName;
        }).join(', ');

        return `${hiddenFiltersText}`;
    };

    const renderFilterResults = (filteredReviews: SessionReview[], categoryCount: number, categoryName: string) => {
        // Use immediate values for instant UI feedback on filter presence
        const hasActiveFilters = selectedTags.length > 0 || excludedTags.length > 0 || searchFieldValue?.length > 0;
        if (!hasActiveFilters) return null;

        return (
            <CollapsingSectionSubtitle hasMatches={filteredReviews.length > 0}>
                {filteredReviews.length > 0 ? (
                    <FilterResultsContainer>
                        <FilterResultsText>{`${filteredReviews.length}`}</FilterResultsText>
                        <FilterTagsDisplay>
                            {selectedTags.filter(tag => {
                                if (tag === 'NO_TAGS_AVAILABLE') {
                                    return filteredReviews.some(review => !review.tags || review.tags.length === 0);
                                }
                                return filteredReviews.some(review => review.tags && review.tags.includes(tag));
                            }).map((tag) => renderFilterTagPill(tag, 'include'))}
                            {excludedTags.filter(tag => {
                                // Show excluded tags that would have been present without the exclusion
                                if (tag === 'NO_TAGS_AVAILABLE') {
                                    return sessionReviews.some(review => !review.tags || review.tags.length === 0);
                                }
                                return sessionReviews.some(review => review.tags && review.tags.includes(tag));
                            }).map((tag) => renderFilterTagPill(tag, 'exclude'))}
                        </FilterTagsDisplay>
                        <FilterResultsText>
                            {`${filteredReviews.length === 1 ? 'engagement' : 'engagements'}`}
                            {/* percentage out of category count */}
                            {categoryCount > 0 ? ` (${((filteredReviews.length / categoryCount) * 100).toFixed(0)}% of ${categoryName})` : ''}
                        </FilterResultsText>
                    </FilterResultsContainer>
                ) : (
                    <FilterResultsContainer>
                        <FilterResultsText>
                            {selectedTags.length === 0 && excludedTags.length === 0 ? 'No tags selected' : 'No'}
                        </FilterResultsText>
                        {(selectedTags.length > 0 || excludedTags.length > 0) && (
                            <>
                                <FilterTagsDisplay>
                                    {sortTagsByPriority(selectedTags, featureFlag).map((tag) => renderFilterTagPill(tag, 'include'))}
                                    {sortTagsByPriority(excludedTags, featureFlag).map((tag) => renderFilterTagPill(tag, 'exclude'))}
                                </FilterTagsDisplay>
                                <FilterResultsText>engagements</FilterResultsText>
                            </>
                        )}
                    </FilterResultsContainer>
                )}
            </CollapsingSectionSubtitle>
        );
    };

    const renderFilterTagPill = (tag: string, mode: 'include' | 'exclude' = 'include') => {
        if (tag === 'NO_TAGS_AVAILABLE') {
            return (
                <FilterTagPill key={tag} isExcluded={mode === 'exclude'}>
                    {mode === 'exclude' ? (
                        <>
                            <NotPrefix>NOT </NotPrefix>
                            Untagged
                        </>
                    ) : (
                        'Untagged'
                    )}
                </FilterTagPill>
            );
        }
        const colors = getTagColors(tag);
        return (
            <FilterTagPill key={tag} tagColors={colors} isExcluded={mode === 'exclude'}>
                {mode === 'exclude' ? (
                    <>
                        <NotPrefix>NOT </NotPrefix>
                        {getTagName(tag, featureFlag)}
                    </>
                ) : (
                    getTagName(tag, featureFlag)
                )}
            </FilterTagPill>
        );
    };

    const renderSessionCard = (review: SessionReview, index: number, categoryPrefix: string) => {
        const categoryPlaceholders = ['IMMEDIATE_TRANSFER', 'NO_INTERACTION', 'No summary'];
        const hasSummary = review.summary && review.summary.trim() !== '' && !categoryPlaceholders.includes(review.summary.trim());
        const hasBeenToggled = collapsedCards.includes(review.sessionId);

        let isCollapsed: boolean;
        if (review.category === "NO_INTERACTION" && index === 0) {
            isCollapsed = hasBeenToggled;
        } else {
            isCollapsed = !hasBeenToggled;
        }

        const phoneNumber = getSessionPhoneNumber(review);
        const lastFourDigits = formatLastFourDigits(phoneNumber);
        const applicationIdForLink = review.applicationId || props.applicationId;

        // Find the most recent sessionId for this multi-call session (if applicable)
        let sessionIdForLink = review.sessionId;
        if (Array.isArray(review.mergedSessions) && review.mergedSessions.length > 1) {
            const latest = review.mergedSessions.reduce((latest, curr) => {
                if (!curr.startDate) return latest;
                if (!latest.startDate) return curr;
                return new Date(curr.startDate).getTime() > new Date(latest.startDate).getTime() ? curr : latest;
            }, review.mergedSessions[0]);
            sessionIdForLink = latest.sessionId;
        }
        const getUserIdSearchFromReview = (review: SessionReview) => {
            // if user id is all numbers return it
            if (/^\d+$/.test(review.userId)) {
                // get last 6 digits
                const lastSixDigits = review.userId.slice(-6);
                return `&initialSearch=${lastSixDigits}`;
            }
            return '';
        }
        return (
            <CollapsibleSessionCard key={`${categoryPrefix}-${review.sessionId}-${index}`}>
                <SessionLink
                    to={`/v/apps/${applicationIdForLink}/analytics/sessions?sessionId=${sessionIdForLink}${getUserIdSearchFromReview(review)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <SessionRow isCollapsed={isCollapsed}>
                        <SessionLeftSide>
                            <SessionTimestamp>{getSessionTimeStamp(review)}</SessionTimestamp>
                            {lastFourDigits && <PhoneNumberDisplay>•••{lastFourDigits}</PhoneNumberDisplay>}
                        </SessionLeftSide>
                        <SessionRightSide>
                            {renderSessionControls(review)}
                            {renderSessionTags(review)}
                            <CollapseButton
                                onClick={(e) => toggleCardCollapse(review.sessionId, e)}
                                isCollapsed={isCollapsed}
                            >
                                <ToggleIndicator direction={isCollapsed ? 'up' : 'down'} />
                            </CollapseButton>
                        </SessionRightSide>
                    </SessionRow>
                    {!isCollapsed && (
                        <>
                            {hasSummary ? (
                                (() => {
                                    const formattedText = formatSummaryText(review.summary);
                                    return (
                                        <SummaryDisplay>
                                            {formattedText.split('\n').map((line, index) => (
                                                <SummaryLine key={index}>{line || '\u00A0'}</SummaryLine>
                                            ))}
                                        </SummaryDisplay>
                                    );
                                })()
                            ) : (
                                (() => {
                                    // Use summary value as category if it's a category placeholder, otherwise use review.category
                                    const effectiveCategory = ['IMMEDIATE_TRANSFER', 'NO_INTERACTION'].includes(review.summary?.trim())
                                        ? review.summary.trim()
                                        : review.category;
                                    if (effectiveCategory === "NO_INTERACTION") {
                                        return (
                                            <SummaryDisplay>
                                                • {featureFlag === "HEALTHCARE" ? "The patient didn't interact with the assistant." : "The customer didn't interact with the assistant."}
                                            </SummaryDisplay>
                                        );
                                    } else if (effectiveCategory === "IMMEDIATE_TRANSFER") {
                                        return (
                                            <SummaryDisplay>
                                                • {featureFlag === "HEALTHCARE" ? "The patient immediately asked to transfer." : "The customer immediately asked to transfer."}
                                            </SummaryDisplay>
                                        );
                                    } else {
                                        return (
                                            <SummaryDisplay>
                                                Summary unavailable
                                            </SummaryDisplay>
                                        );
                                    }
                                })()
                            )}
                        </>
                    )}
                </SessionLink>
            </CollapsibleSessionCard>
        );
    };

    const renderSessionControls = (review: SessionReview) => {
        const isMulti = Array.isArray(review.mergedSessions) && review.mergedSessions.length > 1;
        return (
            <SessionControls>
                <SmallSessionButton
                    themes={['white-small']}
                    text={
                        isMulti
                            ? (visibleSessionIds.includes(review.sessionId) ? 'Hide Session IDs' : 'Show Session IDs')
                            : (visibleSessionIds.includes(review.sessionId) ? 'Hide Session ID' : 'Show Session ID')
                    }
                    onClick={(e) => toggleSessionIdVisibility(review.sessionId, e)}
                />
                {visibleSessionIds.includes(review.sessionId) && (
                    <>
                        {isMulti ? (
                            <SessionIdList>
                                {review.mergedSessions!.map((s, idx) => (
                                    <SessionIdDisplay key={s.sessionId}>
                                        {s.sessionId}
                                        {copiedSessionId === s.sessionId && <CopiedIndicator>Copied!</CopiedIndicator>}
                                        <SmallCopyButton
                                            themes={['white-icon']}
                                            icon={copyIcon}
                                            onClick={(e) => copySessionId(s.sessionId, e)}
                                        />
                                    </SessionIdDisplay>
                                ))}
                            </SessionIdList>
                        ) : (
                            <SessionIdDisplay>
                                {review.sessionId}
                                {copiedSessionId === review.sessionId && <CopiedIndicator>Copied!</CopiedIndicator>}
                                <SmallCopyButton
                                    themes={['white-icon']}
                                    icon={copyIcon}
                                    onClick={(e) => copySessionId(review.sessionId, e)}
                                />
                            </SessionIdDisplay>
                        )}
                    </>
                )}
            </SessionControls>
        );
    };

    const renderSessionTags = (review: SessionReview) => (
        <TagsWrap>
            {review.tags && review.tags.length > 0 ? (
                <SessionTags showVfyTags={props.crossOrgMode} tags={review.tags} featureFlag={featureFlag} />
            ) : (
                <NoTagsPill>Untagged</NoTagsPill>
            )}
        </TagsWrap>
    );

    const renderPaginationControls = (totalSessions: number, category: string, position: 'top' | 'bottom' = 'bottom') => {
        const totalPages = getTotalPages(totalSessions);
        const currentPage = getCurrentPageForCategory(category);

        if (totalPages <= 1) return <EmptyPaginationContainer />;

        const showingFrom = (currentPage - 1) * SESSIONS_PER_PAGE + 1;
        const showingTo = Math.min(currentPage * SESSIONS_PER_PAGE, totalSessions);

        return (
            <PaginationContainer isTop={position === 'top'}>
                <PaginationInfo>
                    Showing {showingFrom}-{showingTo} of {totalSessions} calls
                </PaginationInfo>
                <PaginationControls>
                    <PaginationButton
                        themes={['white-small']}
                        text="Previous"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPageForCategory(category, currentPage - 1)}
                    />
                    <PageNumbers>
                        {(() => {
                            const pages = [];
                            const maxPagesToShow = 5;
                            let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                            let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                            // Adjust start page if we're near the end
                            if (endPage - startPage + 1 < maxPagesToShow) {
                                startPage = Math.max(1, endPage - maxPagesToShow + 1);
                            }

                            // Show first page and ellipsis if needed
                            if (startPage > 1) {
                                pages.push(
                                    <PageNumber
                                        key={1}
                                        active={false}
                                        onClick={() => setCurrentPageForCategory(category, 1)}
                                    >
                                        1
                                    </PageNumber>
                                );
                                if (startPage > 2) {
                                    pages.push(<PageEllipsis key="ellipsis-start">...</PageEllipsis>);
                                }
                            }

                            // Show page numbers
                            for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                    <PageNumber
                                        key={i}
                                        active={i === currentPage}
                                        onClick={() => setCurrentPageForCategory(category, i)}
                                    >
                                        {i}
                                    </PageNumber>
                                );
                            }

                            // Show ellipsis and last page if needed
                            if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                    pages.push(<PageEllipsis key="ellipsis-end">...</PageEllipsis>);
                                }
                                pages.push(
                                    <PageNumber
                                        key={totalPages}
                                        active={false}
                                        onClick={() => setCurrentPageForCategory(category, totalPages)}
                                    >
                                        {totalPages}
                                    </PageNumber>
                                );
                            }

                            return pages;
                        })()}
                    </PageNumbers>
                    <PaginationButton
                        themes={['white-small']}
                        text="Next"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPageForCategory(category, currentPage + 1)}
                    />
                </PaginationControls>
            </PaginationContainer>
        );
    };

    const renderSessionList = (filteredReviews: SessionReview[], categoryPrefix: string = '') => {
        return (
            <SessionList>
                {filteredReviews.length === 0 ? (
                    <NoDataMessage>
                        {categoryPrefix === 'uncategorized'
                            ? 'No calls found with the current filters.'
                            : categoryPrefix === ''
                                ? 'No call data is available for the selected time period. Try adjusting your date range or check back later.'
                                : 'No calls found for this category with the current filters.'
                        }
                    </NoDataMessage>
                ) : (
                    filteredReviews.map((review, index) => renderSessionCard(review, index, categoryPrefix))
                )}
            </SessionList>
        );
    };

    const renderSessionListSorted = (filteredReviews: SessionReview[], categoryPrefix: string = '') => {
        if (filteredReviews.length === 0) {
            return (
                <SessionList>
                    <NoDataMessage>
                        {categoryPrefix === 'uncategorized'
                            ? 'No calls found with the current filters.'
                            : categoryPrefix === ''
                                ? 'No call data is available for the selected time period. Try adjusting your date range or check back later.'
                                : 'No calls found for this category with the current filters.'
                        }
                    </NoDataMessage>
                </SessionList>
            );
        }

        // Sort sessions by date (newest first)
        const sortedSessions = [...filteredReviews].sort((a, b) => {
            // In cross-org mode, use crossOrgSessions; otherwise use sessionContainer.sessions
            const sessions = props.crossOrgMode ? crossOrgSessions : sessionContainer.sessions;
            const sessionA = sessions.find(s => s.sessionId === a.sessionId);
            const sessionB = sessions.find(s => s.sessionId === b.sessionId);

            const dateA = sessionA?.startDate ? new Date(sessionA.startDate) : new Date(0);
            const dateB = sessionB?.startDate ? new Date(sessionB.startDate) : new Date(0);

            // Sort by date descending (newest first)
            return dateB.getTime() - dateA.getTime();
        });

        // Get paginated sessions for this category
        const paginatedSessions = getPaginatedSessions(sortedSessions, categoryPrefix || 'uncategorized');

        return (
            <>
                {renderPaginationControls(sortedSessions.length, categoryPrefix || 'uncategorized', 'top')}
                <SessionList>
                    {paginatedSessions.map((review, index) => renderSessionCard(review, index, categoryPrefix))}
                </SessionList>
                {renderPaginationControls(sortedSessions.length, categoryPrefix || 'uncategorized', 'bottom')}
            </>
        );
    };

    const renderCategory = (category: string) => {
        const filteredReviews = getCachedFilteredReviews(category);
        const categoryStats = sessionStatistics.categories.find((c) => c.key === category);

        if (!categoryStats) return null;

        const categoryPercent = parseFloat(categoryStats.percentage) * 100;
        const categoryCount = categoryStats.count;
        const hasActiveFilters = debouncedSelectedTags.length > 0 || debouncedExcludedTags.length > 0 || debouncedSearchFieldValue?.length > 0;
        const shouldAutoExpand = hasActiveFilters && filteredReviews.length > 0;
        const shouldForceClose = hasActiveFilters && filteredReviews.length === 0;

        return (
            <StyledContentCollapsingSection
                key={category}
                index={0}
                bottom={indexOf(availableCategories, category) === availableCategories.length - 1}
                isCollapsedByDefault={true}
                forceOpen={shouldAutoExpand}
                forceClose={shouldForceClose}
                title={getCategoryName(category, featureFlag)}
                icon={outputIcon}
                id={`category-${category}`}
                titleLeftAdditionalComponent={
                    <TitleStatsText>
                        {`${categoryCount} | ${categoryPercent.toFixed(0)}% of total`}
                    </TitleStatsText>
                }
                titleRightRowComponent={renderFilterResults(filteredReviews, categoryCount, getCategoryName(category, featureFlag))}
            >
                <ScrollableSessionContainer>
                    <CustomScrollbars
                        autoHide
                        autoHeight
                        autoHeightMax="calc(40vh)"
                        autoHeightMin="calc(40vh)"
                        hideHorizontalScrollbar={true}
                    >
                        {renderSessionListSorted(filteredReviews, category)}
                    </CustomScrollbars>
                </ScrollableSessionContainer>
            </StyledContentCollapsingSection>
        );
    };

    const renderNoDataFallback = () => {
        if (sessionReviews.length === 0) {
            return (
                <StyledContentCollapsingSection
                    key="no-data"
                    index={0}
                    bottom={true}
                    isCollapsedByDefault={true}
                    title="No Call Data Available"
                    icon={outputIcon}
                    id="category-no-data"
                    titleRightRowComponent={
                        <CollapsingSectionSubtitle>
                            0 total calls | 0%
                        </CollapsingSectionSubtitle>
                    }
                >
                    <ScrollableSessionContainer>
                        <CustomScrollbars
                            autoHide
                            autoHeight
                            autoHeightMax="calc(100vh - 100px)"
                            hideHorizontalScrollbar={true}
                        >
                            {renderSessionList([], '')}
                        </CustomScrollbars>
                    </ScrollableSessionContainer>
                </StyledContentCollapsingSection>
            );
        }

        // Show uncategorized sessions
        const filteredUncategorizedReviews = getCachedFilteredReviews();
        const hasActiveFilters = debouncedSelectedTags.length > 0 || debouncedExcludedTags.length > 0 || debouncedSearchFieldValue?.length > 0;
        const shouldAutoExpand = hasActiveFilters && filteredUncategorizedReviews.length > 0;
        const shouldForceClose = hasActiveFilters && filteredUncategorizedReviews.length === 0;

        return (
            <StyledContentCollapsingSection
                key="uncategorized"
                index={0}
                bottom={true}
                isCollapsedByDefault={true}
                forceOpen={shouldAutoExpand}
                forceClose={shouldForceClose}
                title="Uncategorized Calls"
                icon={outputIcon}
                id="category-uncategorized"
                titleLeftAdditionalComponent={
                    <TitleStatsText>
                        {`${sessionReviews.length} ${sessionReviews.length === 1 ? 'total' : 'total'} | 100%`}
                    </TitleStatsText>
                }
                titleRightRowComponent={renderFilterResults(filteredUncategorizedReviews, 0, null)}
            >
                <ScrollableSessionContainer>
                    <CustomScrollbars
                        autoHide
                        autoHeight
                        autoHeightMax="calc(100vh - 100px)"
                        hideHorizontalScrollbar={true}
                    >
                        {renderSessionListSorted(filteredUncategorizedReviews, 'uncategorized')}
                    </CustomScrollbars>
                </ScrollableSessionContainer>
            </StyledContentCollapsingSection>
        );
    };

    const handleResetFilters = async () => {
        setResettingFilters(true);
        try {
            // Add a small delay to provide visual feedback
            await new Promise(resolve => setTimeout(resolve, 300));

            setSelectedTags([]);
            setExcludedTags([]);
            setSearchFieldValue("");
            // Reset pagination when filters are reset
            setCategoryCurrentPages({});
        } finally {
            setResettingFilters(false);
        }
    };

    const getCurrentPageForCategory = (category: string): number => {
        return categoryCurrentPages[category] || 1;
    };

    const setCurrentPageForCategory = (category: string, page: number) => {
        setCategoryCurrentPages(prev => ({
            ...prev,
            [category]: page
        }));

        // Scroll to top of category after page change
        setTimeout(() => {
            const categoryElement = document.getElementById(`category-${category}`);
            if (categoryElement) {
                categoryElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }, 100); // Small delay to ensure DOM has updated
    };

    const getPaginatedSessions = (sessions: SessionReview[], category: string) => {
        const currentPage = getCurrentPageForCategory(category);
        const startIndex = (currentPage - 1) * SESSIONS_PER_PAGE;
        const endIndex = startIndex + SESSIONS_PER_PAGE;
        return sessions.slice(startIndex, endIndex);
    };

    const getTotalPages = (totalSessions: number): number => {
        return Math.ceil(totalSessions / SESSIONS_PER_PAGE);
    };

    return (
        <GenerativeReportContainer>
            <GenerativeSessionReviewRow>
                {availableTags.length === 0 ? (
                    <NoTagsMessage>
                        No tag data available for the selected time period.
                    </NoTagsMessage>
                ) : (
                    availableTags
                        .filter(tag => tag !== 'NO_TAGS_AVAILABLE')
                        .filter(tag => TAGS_WITH_SUMMARY_CARDS.includes(tag))
                        .map((tag) => {
                            const tagStats = sessionStatistics.tags.find((t) => t.key === tag);
                            if (!tagStats) return null;

                            const tagPercentage = parseFloat(tagStats.percentage) * 100;
                            const tagCount = tagStats.count;

                            return (
                                <StyledTotalCard
                                    key={tag}
                                    title={getTagName(tag, featureFlag)}
                                    value={`${tagCount} | ${tagPercentage.toFixed(0)}%`}
                                    showValue={false}
                                    showPercentage={false}
                                    icon={getIconByTagLabel(tag)}
                                    subValue='of all calls'
                                />
                            );
                        })
                )}
            </GenerativeSessionReviewRow>
            <CallBreakdownSectionWrapper>
                <CallBreakdownHeader>
                    <CallBreakdownTitleSection>
                        <CallBreakdownTitle>{`Engagement Breakdown`}</CallBreakdownTitle>
                        {(props.crossOrgMode || props.currentOrgMode) && (
                            <LoadEngagementsButton
                                themes={['primary', 'start']}
                                text="Load engagements"
                                onClick={handleLoadEngagements}
                                disabled={!canLoadEngagements}
                                loading={processingReviews}
                            />
                        )}
                        {((props.crossOrgMode || props.currentOrgMode) && sessionReviews.length > 0) ? (
                            <ExportCSVButton
                                themes={['white-small']}
                                text="Export to CSV"
                                onClick={handleExportCSV}
                                disabled={processingReviews || sessionReviews.length === 0}
                            />) : <></>
                        }
                        {(props.crossOrgMode || props.currentOrgMode) && hasLoadedCrossOrgData && loadingLog.length > 0 && (
                            <LoadingLogBox ref={loadingLogRef}>
                                {loadingLog.map((logEntry, index) => (
                                    <LogEntry key={index}>{logEntry}</LogEntry>
                                ))}
                            </LoadingLogBox>
                        )}
                    </CallBreakdownTitleSection>
                    <CallBreakdownControls>
                        {((selectedTags.length > 0 || excludedTags.length > 0 || searchFieldValue?.length > 0) &&
                            (filteredStatistics || searchFieldValue?.length > 0)) && (
                                <FilteredStatsDisplay>
                                    <FilteredStatsTextContainer>
                                        <FilteredStatsText>
                                            {filteredStatistics ?
                                                `${filteredStatistics.count} engagement${filteredStatistics.count > 1 ? 's' : ''} | ${filteredStatistics.percentage}% match filter${filteredStatistics.count > 1 ? 's' : ''}:` :
                                                'Filtering...'
                                            }
                                        </FilteredStatsText>
                                    </FilteredStatsTextContainer>
                                    <FilteredTagsContainer>
                                        {(() => {
                                            const allTags = [
                                                ...sortTagsByPriority(selectedTags, featureFlag).map(tag => ({ tag, mode: 'include' as const })),
                                                ...sortTagsByPriority(excludedTags, featureFlag).map(tag => ({ tag, mode: 'exclude' as const })),
                                                ...(searchFieldValue ? [{ tag: `Search: "${searchFieldValue}"`, mode: 'search' as const }] : [])
                                            ];

                                            const maxVisibleTags = 4;
                                            const visibleTags = allTags.slice(0, maxVisibleTags);
                                            const hiddenTags = allTags.slice(maxVisibleTags);
                                            const hiddenCount = hiddenTags.length;

                                            return (
                                                <>
                                                    {visibleTags.map(({ tag, mode }, index) => {
                                                        if (mode === 'search') {
                                                            return (
                                                                <FilteredSearchPill key={`search-${index}`}>
                                                                    {tag}
                                                                </FilteredSearchPill>
                                                            );
                                                        }
                                                        return (
                                                            <FilteredTagPill
                                                                key={`${mode}-${tag}`}
                                                                tagColors={tag === 'NO_TAGS_AVAILABLE' ? undefined : getTagColors(tag)}
                                                                isExcluded={mode === 'exclude'}
                                                            >
                                                                {mode === 'exclude' && <NotPrefix>NOT </NotPrefix>}
                                                                {tag === 'NO_TAGS_AVAILABLE' ? 'Untagged' : getTagName(tag, featureFlag)}
                                                            </FilteredTagPill>
                                                        );
                                                    })}
                                                    {hiddenCount > 0 && (
                                                        <TooltipWrapper
                                                            text={renderHiddenFiltersTooltip(hiddenTags)}
                                                            place="top"
                                                            mouseEnterDelay={0.3}
                                                        >
                                                            <MoreFiltersIndicator>
                                                                +{hiddenCount} more filter{hiddenCount > 1 ? 's' : ''}
                                                            </MoreFiltersIndicator>
                                                        </TooltipWrapper>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </FilteredTagsContainer>
                                </FilteredStatsDisplay>
                            )}
                        <FilterModalButton
                            themes={['primary', 'start']}
                            text={`Filter calls`}
                            onClick={handleFilterModalOpen}
                            disabled={processingReviews || (props.crossOrgMode && !hasLoadedCrossOrgData)}
                        />
                        <ResetFiltersButton
                            themes={['white-small']}
                            text="Reset filters"
                            onClick={handleResetFilters}
                            loading={resettingFilters}
                            disabled={selectedTags.length === 0 && excludedTags.length === 0 && !searchFieldValue}
                        />
                        <CustomSearchWrapper
                            onClick={stop}
                            onMouseDown={stop}
                        >
                            <CustomSearch
                                id="searchField"
                                name="searchField"
                                placeholder={props.crossOrgMode || props.currentOrgMode ? "Search by ID, phone, or app name" : "Enter ID or last 4 of phone"}
                                value={searchFieldValue}
                                onChange={(e) => setSearchFieldValue(e.target.value)}
                                boxType="square"
                            />
                        </CustomSearchWrapper>
                    </CallBreakdownControls>
                </CallBreakdownHeader>
            </CallBreakdownSectionWrapper>
            {availableCategories.length === 0 && !processingReviews && hasInitiallyLoaded ? (
                <FullWidthSectionWrapper>
                    {renderNoDataFallback()}
                </FullWidthSectionWrapper>
            ) : (
                <>
                    {availableCategories.map((categoryKey) => (
                        <FullWidthSectionWrapper key={categoryKey}>
                            {renderCategory(categoryKey)}
                        </FullWidthSectionWrapper>
                    ))}
                    {((debouncedSelectedTags.length > 0) || debouncedExcludedTags.length > 0 || debouncedSearchFieldValue?.length > 0) &&
                        availableCategories.every(category => getCachedFilteredReviews(category).length === 0) && (
                            <FullWidthSectionWrapper>
                                <FilterNoResultsMessage>
                                    <NoDataMessage>
                                        No calls match the current filters. Try adjusting your tag selection or search criteria to see results.
                                    </NoDataMessage>
                                </FilterNoResultsMessage>
                            </FullWidthSectionWrapper>
                        )}
                </>
            )}
            <TagFilterModal
                isVisible={isFilterModalOpen}
                onClose={handleFilterModalClose}
                tags={availableTags}
                tagsForDisplay={tagsForDisplay}
                isCrossOrgMode={props.crossOrgMode}
                selectedTags={selectedTagsSet}
                excludedTags={excludedTagsSet}
                onToggle={toggleTag}
                onApply={handleFilterModalApply}
                isLoading={isApplyingFilters}
                application={props.application}
                setFilterLogic={setFilterLogic}
            />
        </GenerativeReportContainer>
    );
};

export default GenerativeReportTable;
